import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PhoneOff, Mic, MicOff, Volume2 } from "lucide-react";
import { useVapi } from "@/hooks/useVapi";
import { useVapiDataCollection } from "@/hooks/useVapiDataCollection";
import AnimatedGradientBackground from "@/components/ui/animated-gradient-background";
import { VoicePoweredOrb } from "@/components/ui/voice-powered-orb";
import { DataConfirmationModal } from "@/components/ui/data-confirmation-modal";

const Call = () => {
  const navigate = useNavigate();
  
  // Set up data collection
  const {
    collectedData,
    isDataComplete,
    isSending,
    sendError,
    handleVapiData,
    sendDataToTeam,
    showConfirmation,
    setShowConfirmation,
    resetConfirmationDetection,
    updateCollectedData,
  } = useVapiDataCollection();

  // Set up VAPI with data collection handler
  const { startCall, endCall, sendUserMessage, status, isCallActive } = useVapi(handleVapiData);
  const [callStarted, setCallStarted] = useState(false);
  const [voiceDetected, setVoiceDetected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const disconnectAudioRef = useRef<HTMLAudioElement | null>(null);

  // NOTE: We intentionally DO NOT autoplay audio or autostart the call on page load.
  // Most browsers block audio playback and sometimes WebRTC/mic flows without a user gesture,
  // which can leave the UI stuck on "Preparing..." in production.


  // Play disconnect sound when call ends
  useEffect(() => {
    if (status === "ended" && callStarted && !disconnectAudioRef.current) {
      // Play the disconnected sound when call ends
      const audio = new Audio("/disconnected.mp3");
      audio.volume = 0.5;
      audio.play().catch((error) => {
        console.log("Disconnect audio play failed:", error);
      });
      disconnectAudioRef.current = audio;
      // Reset ref after sound finishes (approx 2 seconds)
      setTimeout(() => {
        disconnectAudioRef.current = null;
      }, 2500);
    }
    // Reset audio ref when status changes from ended/idle back to idle
    if (status === "idle" && disconnectAudioRef.current) {
      disconnectAudioRef.current = null;
    }
  }, [status, callStarted]);

  const handleStartCall = async () => {
    if (callStarted || status === "connecting" || status === "active") return;

    // User gesture: play the Siri sound
    try {
      const audio = new Audio("/apple-siri.mp3");
      audio.volume = 0.5;
      await audio.play();
      audioRef.current = audio;
    } catch (error) {
      console.log("Audio play failed:", error);
    }

    try {
      // Prompt mic permission on user gesture (improves production reliability)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch (error) {
      console.warn("Microphone permission denied:", error);
      // Still try to start the call; Vapi will also request mic.
    }

    setCallStarted(true);
    const assistantId = import.meta.env.VITE_VAPI_ASSISTANT_ID || "";
    startCall(assistantId);
  };

  const handleEndCall = () => {
    endCall();
    // Mark that we're manually ending, so we don't auto-redirect
    callWasActiveRef.current = false;
    // Navigate back to home after ending call
    setTimeout(() => {
      navigate("/");
    }, 500);
  };

  const handleToggleMute = () => {
    setIsMuted(!isMuted);
    // TODO: Implement actual mute functionality with VAPI
    console.log("Mute toggled:", !isMuted);
  };

  // Track if call was ever active to know when to redirect
  const callWasActiveRef = useRef(false);
  
  useEffect(() => {
    if (status === "active") {
      callWasActiveRef.current = true;
    }
  }, [status]);

  // Track if we've sent data to prevent showing modal again
  const hasSentRef = useRef(false);
  useEffect(() => {
    if (isSending) {
      hasSentRef.current = true;
    }
  }, [isSending]);

  // Handle confirmation modal actions
  const handleConfirmSend = async () => {
    try {
      await sendDataToTeam();
      hasSentRef.current = true; // Mark as sent
      // Close modal after successful send
      setTimeout(() => {
        setShowConfirmation(false);
      }, 1500);
    } catch (error) {
      // Keep modal open on error so user can retry
      console.error("Failed to send data:", error);
    }
  };

  // Cancel is now handled via voice - modal will stay open until user confirms or VAPI closes it
  // User can update information by speaking

  // Debug: Log collected data
  useEffect(() => {
    if (Object.keys(collectedData).length > 0) {
      console.log("📊 Current collected data:", collectedData);
      console.log("📊 Is data complete:", isDataComplete);
    }
  }, [collectedData, isDataComplete]);

  // Fallback: Show modal after data is collected for a few seconds (if call is still active)
  // This ensures the modal shows even if call-end detection doesn't work
  useEffect(() => {
    if (
      isDataComplete &&
      isCallActive &&
      !showConfirmation &&
      !hasSentRef.current &&
      Object.keys(collectedData).length > 0
    ) {
      console.log("⏰ Setting fallback timer to show modal after data collection...");
      const timer = setTimeout(() => {
        // Only show if we haven't already shown it and call is still active or just ended
        if (!showConfirmation && !hasSentRef.current && (isCallActive || status === "ended")) {
          console.log("📋 Fallback: Opening confirmation modal");
          setShowConfirmation(true);
        }
      }, 3000); // Show after 3 seconds of having data

      return () => clearTimeout(timer);
    }
  }, [isDataComplete, isCallActive, showConfirmation, collectedData, status]);

  // Show confirmation modal when call ends and we have collected data
  useEffect(() => {
    console.log("🔍 Modal trigger check:", {
      status,
      isDataComplete,
      showConfirmation,
      hasSent: hasSentRef.current,
      collectedDataKeys: Object.keys(collectedData),
    });

    // Show modal when call ends OR when status becomes idle after being active
    if (
      (status === "ended" || (status === "idle" && callWasActiveRef.current)) &&
      isDataComplete && 
      !showConfirmation && 
      !hasSentRef.current &&
      Object.keys(collectedData).length > 0
    ) {
      console.log("✅ Conditions met - showing confirmation modal...");
      // Show modal after a short delay
      const timer = setTimeout(() => {
        console.log("📋 Opening confirmation modal now");
        setShowConfirmation(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status, isDataComplete, showConfirmation, collectedData]);

  // Reset when call ends (only redirect if call was actually active and then ended)
  useEffect(() => {
    // Only redirect if:
    // 1. Call was active at some point (callWasActiveRef.current === true)
    // 2. Status is "ended" (call was active and then ended)
    // 3. Modal is closed (either sent or cancelled) OR no data was collected
    if (
      callWasActiveRef.current && 
      status === "ended" && 
      !showConfirmation && // Modal must be closed
      (hasSentRef.current || !isDataComplete) // Either sent/cancelled or no data
    ) {
      // Wait a bit before redirecting so user can see the disconnect sound play
      const timeoutId = setTimeout(() => {
        navigate("/");
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
    
    // Don't redirect on idle state - let user manually end or stay on page
    // This prevents premature redirects during initialization or errors
  }, [status, navigate, isDataComplete, showConfirmation]);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Gradient Background - Green/Cyan Palette */}
      <AnimatedGradientBackground
        gradientColors={[
          "#0A0A0A",
          "#10b981", // emerald
          "#14b8a6", // teal
          "#06b6d4", // cyan
          "#3b82f6", // blue
          "#22d3ee", // cyan-400
          "#34d399" // emerald-300
        ]}
        Breathing={true}
      />

      <div className="relative z-10 flex flex-col h-full overflow-hidden">
        {/* Orb - Centered with fixed dimensions */}
        <div className="flex-1 flex items-center justify-center px-4 min-h-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="w-96 h-96 relative flex-shrink-0"
            style={{ willChange: "transform" }}
          >
            <VoicePoweredOrb
              enableVoiceControl={isCallActive}
              onVoiceDetected={setVoiceDetected}
            />
          </motion.div>
        </div>

        {/* Bottom Section - Clean Apple Style with fixed height */}
        <div className="pb-12 md:pb-16 px-4 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-md mx-auto"
            style={{ minHeight: "140px" }}
          >
            {status === "connecting" ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center space-x-1.5">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                  <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
                </div>
                <p className="text-sm text-white/70 font-medium tracking-wide">Connecting...</p>
              </div>
            ) : isCallActive ? (
              <div className="space-y-6">
                {/* Status */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      className="w-2 h-2 bg-emerald-400 rounded-full"
                    />
                    <p className="text-sm text-white/90 font-medium tracking-wide uppercase">Active</p>
                  </div>
                </div>

                {/* Buttons - Green Glassmorphism Style */}
                <div className="flex flex-col items-center gap-4 pt-6">
                  {/* End Call Button - Green Glassmorphism */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleEndCall}
                    className="relative w-64 h-14 rounded-full overflow-hidden group"
                    style={{ willChange: "transform" }}
                  >
                    {/* Glassmorphism Background */}
                    <div className="absolute inset-0 bg-emerald-500/20 backdrop-blur-xl border border-emerald-400/30 rounded-full" />
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-cyan-500/20 opacity-50" />

                    {/* Content */}
                    <div className="relative h-full flex items-center justify-center gap-2">
                      <PhoneOff className="h-5 w-5 text-white group-hover:rotate-12 transition-transform duration-200" />
                      <span className="text-white font-semibold text-base tracking-wide">End Call</span>
                    </div>

                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </motion.button>

                  {/* Mute/Unmute Button - Secondary */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleToggleMute}
                    className="relative w-14 h-14 rounded-full overflow-hidden group"
                    style={{ willChange: "transform" }}
                  >
                    {/* Glassmorphism Background */}
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full" />

                    {/* Content */}
                    <div className="relative h-full flex items-center justify-center">
                      {isMuted ? (
                        <MicOff className="h-6 w-6 text-white/90 group-hover:text-white transition-colors" />
                      ) : (
                        <Mic className="h-6 w-6 text-white/90 group-hover:text-white transition-colors" />
                      )}
                    </div>
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-sm text-white/70 font-medium">
                  {callStarted ? "Tap to retry" : "Tap to start"}
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleStartCall}
                  className="relative w-64 h-14 rounded-full overflow-hidden group mx-auto"
                  style={{ willChange: "transform" }}
                >
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full" />
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/25 to-cyan-500/20 opacity-60" />
                  <div className="relative h-full flex items-center justify-center gap-2">
                    <Volume2 className="h-5 w-5 text-white/90" />
                    <span className="text-white font-semibold text-base tracking-wide">
                      {callStarted ? "Retry call" : "Start call"}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </motion.button>
                <p className="text-xs text-white/50">
                  Click once to enable audio + microphone, then the agent will connect.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Data Confirmation Modal - Updates in real-time as user speaks */}
      <DataConfirmationModal
        open={showConfirmation}
        onOpenChange={(open) => {
          // Only allow VAPI to close the modal via voice, not by clicking outside
          if (!open && !isSending) {
            // Modal can be closed programmatically when data is sent
            setShowConfirmation(false);
          }
        }}
        data={collectedData}
        onConfirm={handleConfirmSend}
        onEndCall={handleEndCall}
        onDataChange={updateCollectedData}
        isSending={isSending}
        sendError={sendError}
      />
    </div>
  );
};

export default Call;

